import React, { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Plus, X, MessageCircle } from "lucide-react";
import { Navbar } from "../components/Navbar";
import { Footer } from "../components/Footer";
import { ProductModal } from "../components/ProductModal";
import { api, MERCH_POSTER } from "../lib/api";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";
import { useCurrency } from "../context/CurrencyContext";
import { toast } from "sonner";

// Posters — local to this page. Flat regional pricing via the app's geo detection:
// NG ₦10,000 · US $10 · CA CA$10 · GB £10 · EU €10 · AU AU$10 · NZ NZ$10.
const POSTERS = [
  { id: "poster-nm-emblem", name: "NAIJAMAN — NM Emblem Portrait", price: 10000,
    image: "https://customer-assets-gfyr7b9c.emergentagent.net/job_naija-universe/artifacts/wbdnc6cs_83CE405E-7C67-4ACC-8F7B-5096AD1A8CC7.PNG" },
  { id: "poster-rain-front", name: "NAIJAMAN Superhero — Rainstorm (Front)", price: 10000,
    image: "https://customer-assets-gfyr7b9c.emergentagent.net/job_naija-universe/artifacts/ubjafdq5_A8D8D174-08AE-482A-ADDF-C561F2B89D6B.PNG" },
  { id: "poster-rain-full", name: "NAIJAMAN Superhero — Rainstorm (Full)", price: 10000,
    image: "https://customer-assets-gfyr7b9c.emergentagent.net/job_naija-universe/artifacts/sr272xo2_9DABC1B2-ABD7-47B7-B066-40B66E42529D.PNG" },
  { id: "poster-above-people", name: "NAIJAMAN — Above the People", price: 10000,
    image: "https://customer-assets-gfyr7b9c.emergentagent.net/job_naija-universe/artifacts/67cbvqt8_B9A91194-F39A-4C94-A7AE-D3C88E6ADF89.PNG" },
  { id: "poster-look-up", name: "NAIJAMAN — Look Up, Nigeria Has a Superhero", price: 10000,
    image: "https://customer-assets-gfyr7b9c.emergentagent.net/job_naija-universe/artifacts/ilgkl2hc_IMG_1881.jpg" },
  { id: "poster-issue-1", name: "NAIJAMAN — Issue #1 Cover Art", price: 10000,
    image: "https://customer-assets-gfyr7b9c.emergentagent.net/job_naija-universe/artifacts/0ous49e0_3A79D89C-4ED2-49FB-B8A4-A647D17B47A8.PNG" },
  { id: "poster-lagos-guardian", name: "NAIJAMAN — Lagos Guardian", price: 10000,
    image: "https://customer-assets-gfyr7b9c.emergentagent.net/job_naija-universe/artifacts/svz22ko6_462A0203-5BDF-4854-A418-7BAAC8D01B12.PNG" },
  { id: "poster-symbol-of-naija", name: "NAIJAMAN — Symbol of Naija", price: 10000,
    image: "https://customer-assets-gfyr7b9c.emergentagent.net/job_naija-universe/artifacts/ldqf97dw_774703E2-B94F-418C-BE80-5B5F132827CE%20%281%29.PNG" },
  { id: "poster-power-surge", name: "NAIJAMAN — Power Surge", price: 10000,
    image: "https://customer-assets-gfyr7b9c.emergentagent.net/job_naija-universe/artifacts/nd4td6kk_7627D38F-6AF4-4A8B-BE80-483F44CB6A42.PNG" },
  { id: "poster-flag-bearer", name: "NAIJAMAN — Flag Bearer", price: 10000,
    image: "https://customer-assets-gfyr7b9c.emergentagent.net/job_naija-universe/artifacts/48fbpxl5_B8A11806-0AF5-47B0-8F2B-E4CEEFBB60E5.PNG" },
];

// T-Shirts — local to this page. Flat regional pricing via the app's geo detection:
// NG ₦15,000 · US $20 · CA CA$20 · GB £20 · EU €20 · AU AU$20 · NZ NZ$20.
const TSHIRTS = [
  { id: "tee-splash-white", name: "NAIJAMAN Splash Action Tee (White)", price: 15000, intl: 20,
    image: "https://customer-assets-gfyr7b9c.emergentagent.net/job_naija-universe/artifacts/7g5hif35_43F77628-9E3F-43CD-BD9F-703FCBBA7497.PNG" },
  { id: "tee-classic-black", name: "NAIJAMAN Classic Pose Tee (Black)", price: 15000, intl: 20,
    image: "https://customer-assets-gfyr7b9c.emergentagent.net/job_naija-universe/artifacts/6fofka4e_64D8FBCB-0449-466D-B7B5-22F15D9786FF.PNG" },
  { id: "tee-superhero-script", name: "NAIJAMAN Superhero Script Tee (Black)", price: 15000, intl: 20,
    image: "https://customer-assets-gfyr7b9c.emergentagent.net/job_naija-universe/artifacts/psby527g_20336A02-D6D4-4CF1-8F8D-0D88EA8F2F0D.PNG" },
  { id: "tee-drip-logo", name: "NAIJAMAN Drip Logo Tee (Black)", price: 15000, intl: 20,
    image: "https://customer-assets-gfyr7b9c.emergentagent.net/job_naija-universe/artifacts/a32nfoj2_8D0F8E6A-EF30-4EBD-A3B8-D5D80B26BB7A.PNG" },
  { id: "tee-hero-stance", name: "NAIJAMAN Hero Stance Tee (Black)", price: 15000, intl: 20,
    image: "https://customer-assets-gfyr7b9c.emergentagent.net/job_naija-universe/artifacts/wgcwh97w_IMG_1944.jpg" },
  { id: "tee-guardian-portrait", name: "NAIJAMAN Guardian Portrait Tee (Black)", price: 15000, intl: 20,
    image: "https://customer-assets-gfyr7b9c.emergentagent.net/job_naija-universe/artifacts/l1ldcw2n_B761140A-B37C-4D94-ABF5-5A69AD179C1E.PNG" },
  { id: "tee-tribal-logo", name: "NAIJAMAN Tribal Logo Tee (Black)", price: 15000, intl: 20,
    image: "https://customer-assets-gfyr7b9c.emergentagent.net/job_naija-universe/artifacts/8g520vfh_ECF6376B-8FC6-4750-AA94-6BDEB31619B9.PNG" },
  { id: "tee-vintage-emblem", name: "NAIJAMAN Vintage Emblem Tee (Black)", price: 15000, intl: 20,
    image: "https://customer-assets-gfyr7b9c.emergentagent.net/job_naija-universe/artifacts/44reph5t_49D4F4B8-180D-4548-83C5-0A7A62F6C538.PNG" },
  { id: "tee-gbas-gbos", name: "NAIJAMAN GBAS! GBOS! Tee (Black)", price: 15000, intl: 20,
    image: "https://customer-assets-gfyr7b9c.emergentagent.net/job_naija-universe/artifacts/jp3lxyev_4A56FD55-700A-446E-91DA-1B08344EC643.PNG" },
  { id: "tee-woto", name: "NAIJAMAN WOTO! WOTO! Tee (Black)", price: 15000, intl: 20,
    image: "https://customer-assets-gfyr7b9c.emergentagent.net/job_naija-universe/artifacts/nbux0qdy_4B6CC66A-4B49-48CC-8006-634EB49BAEC2.PNG" },
];

// Hoodies — local to this page. Flat regional pricing via the app's geo detection:
// NG ₦25,000 · US $35 · CA CA$35 · GB £35 · EU €35 · AU AU$35 · NZ NZ$35.
const HOODIES = [
  { id: "hoodie-splash-white", name: "NAIJAMAN Splash Action Hoodie (White)", price: 25000, intl: 35,
    image: "https://customer-assets-gfyr7b9c.emergentagent.net/job_naija-universe/artifacts/tpyff1y6_A6AC6ABB-55BA-4C7D-BCA9-201D301B0037.PNG" },
  { id: "hoodie-logo-splash-black", name: "NAIJAMAN Logo Splash Hoodie (Black)", price: 25000, intl: 35,
    image: "https://customer-assets-gfyr7b9c.emergentagent.net/job_naija-universe/artifacts/9yxpgsa7_0FC9BD85-F338-45D1-8541-F67362010BA8.PNG" },
  { id: "hoodie-watercolor-white", name: "NAIJAMAN Watercolor Profile Hoodie (White)", price: 25000, intl: 35,
    image: "https://customer-assets-gfyr7b9c.emergentagent.net/job_naija-universe/artifacts/0zqmlmln_3EDF90B0-C218-4F63-92C5-8119C11842F5%20%281%29.PNG" },
  { id: "hoodie-watercolor-black", name: "NAIJAMAN Watercolor Profile Hoodie (Black)", price: 25000, intl: 35,
    image: "https://customer-assets-gfyr7b9c.emergentagent.net/job_naija-universe/artifacts/q3qv6an7_CFF6282B-034A-411D-81C6-F778B54005F4.PNG" },
  { id: "hoodie-front-back-white", name: "NAIJAMAN Front & Back Hoodie (White)", price: 25000, intl: 35,
    image: "https://customer-assets-gfyr7b9c.emergentagent.net/job_naija-universe/artifacts/bcapha0o_F6337288-385F-4098-A241-53523ED0F7EB%20%282%29.PNG" },
  { id: "hoodie-noir-white", name: "NAIJAMAN Noir Watercolour Hoodie (White)", price: 25000, intl: 35,
    image: "https://customer-assets-gfyr7b9c.emergentagent.net/job_naija-universe/artifacts/423hg6lp_BB8B8D5F-9B2B-44B7-AC17-5E0F9A4634D5.PNG" },
  { id: "hoodie-noir-black", name: "NAIJAMAN Noir Watercolour Hoodie (Black)", price: 25000, intl: 35,
    image: "https://customer-assets-gfyr7b9c.emergentagent.net/job_naija-universe/artifacts/nw1zk1cx_7F31FAA1-2E20-41E7-96F7-FB82CD2948FF.PNG" },
];

// Accessories — local to this page, merged into the main grid under the Accessories category.
// Flat regional pricing: pack ₦75,000/$75 · cap ₦20,000/$20 (CA CA$ · GB £ · EU € · AU AU$ · NZ NZ$).
const LOCAL_ACCESSORIES = [
  { id: "collectors-pack", name: "Limited Edition NAIJAMAN Collector's Pack", price: 75000,
    image: "https://customer-assets-gfyr7b9c.emergentagent.net/job_naija-universe/artifacts/fsssyo1z_IMG_1470.PNG",
    hoverImage: "https://customer-assets-gfyr7b9c.emergentagent.net/job_naija-universe/artifacts/tz5486i7_IMG_1467.PNG",
    category: "accessories", local: true, badge: "ONLY 100 AVAILABLE",
    blurb: "Branded water bottle, logo cap & premium tee in a collector's box" },
  { id: "logo-cap", name: "NAIJAMAN Logo Cap", price: 20000,
    image: "https://customer-assets-gfyr7b9c.emergentagent.net/job_naija-universe/artifacts/tgbziwrz_6319FBE1-29D4-4321-9575-5F88863608CC.PNG",
    category: "accessories", local: true },
];

// Main-grid hoodie price override (catalog hoodies): ₦25,000 NG · $35/CA$35/£35/€35/AU$35/NZ$35 elsewhere.
const hoodiePriced = (p) => (/hoodie/i.test(p?.name || "") ? { ...p, price: 25000, intl: 35, local: true } : p);

// Apparel pickers — size & color selection directly on t-shirt/hoodie cards.
const TEE_SIZES = ["S", "M", "L", "XL", "XXL"];
const TEE_COLORS = [
  { name: "Black", hex: "#151515" },
  { name: "White", hex: "#f5f5f5" },
  { name: "Navy", hex: "#1f3557" },
  { name: "Gray", hex: "#6b7280" },
];
const isApparel = (p) => /t-?shirt|tee|hoodie/i.test(p?.name || "");

const ApparelPickers = ({ id, pick, onPick }) => (
  <div className="mt-3 space-y-2" onClick={(e) => e.stopPropagation()}>
    <div className="flex flex-wrap gap-1.5" data-testid={`size-picker-${id}`}>
      {TEE_SIZES.map((s) => (
        <button
          key={s}
          onClick={() => onPick("size", s)}
          className={`min-w-[30px] px-1.5 py-1 text-[11px] font-accent tracking-wide rounded border transition-colors ${pick.size === s ? "bg-emerald-500 border-emerald-500 text-emerald-950" : "border-white/15 text-gray-400 hover:border-emerald-400"}`}
          data-testid={`size-pick-${id}-${s.toLowerCase()}`}
        >
          {s}
        </button>
      ))}
    </div>
    <div className="flex items-center gap-2" data-testid={`color-picker-${id}`}>
      {TEE_COLORS.map((c) => (
        <button
          key={c.name}
          onClick={() => onPick("color", c.name)}
          title={c.name}
          aria-label={`color ${c.name}`}
          style={{ backgroundColor: c.hex }}
          className={`w-5 h-5 rounded-full border transition-all ${pick.color === c.name ? "ring-2 ring-emerald-400 ring-offset-2 ring-offset-black border-white/40" : "border-white/20 hover:border-emerald-400"}`}
          data-testid={`color-pick-${id}-${c.name.toLowerCase()}`}
        />
      ))}
    </div>
  </div>
);

export default function Merch() {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [active, setActive] = useState("all");
  const [selected, setSelected] = useState(null);
  const [posterView, setPosterView] = useState(null);
  const [picks, setPicks] = useState({});
  const getPick = (id) => ({ size: "M", color: "Black", ...picks[id] });
  const setPick = (id, key, val) =>
    setPicks((prev) => ({ ...prev, [id]: { size: "M", color: "Black", ...prev[id], [key]: val } }));
  const { addItem } = useCart();
  const { fmt, country, currency, symbol } = useCurrency();
  // Flat regional pricing for local sections: NG pays the Naira price; everyone else pays the
  // flat international price (intl, defaults to price/1000) — AU/NZ render their own symbols.
  const flatPrice = (p) => {
    const intl = p.intl ?? p.price / 1000;
    if (currency === "NGN") return fmt(p.price);
    if (country === "AU") return `AU$${intl}`;
    if (country === "NZ") return `NZ$${intl}`;
    return `${symbol}${intl}`;
  };

  const { user } = useAuth();
  const [payCfg, setPayCfg] = useState(null);
  const [buyItem, setBuyItem] = useState(null);
  const [buyEmail, setBuyEmail] = useState("");

  useEffect(() => {
    api.get("/config").then(({ data }) => setPayCfg(data)).catch(() => {});
  }, []);

  // Paystack inline popup — loads on demand, charges the flat NGN price.
  const loadPaystack = () =>
    new Promise((resolve, reject) => {
      if (window.PaystackPop) return resolve();
      const s = document.createElement("script");
      s.src = "https://js.paystack.co/v1/inline.js";
      s.async = true;
      s.onload = () => resolve();
      s.onerror = reject;
      document.body.appendChild(s);
    });

  const openPaystack = async (p, email) => {
    try {
      await loadPaystack();
    } catch {
      toast.error("Could not load the payment window. Check your connection and retry.");
      return;
    }
    const variant = isApparel(p) ? `${getPick(p.id).size} / ${getPick(p.id).color}` : null;
    // Charge currency by shopper location: NG pays the Naira price, everyone else pays the USD price.
    const isNg = currency === "NGN";
    const handler = window.PaystackPop.setup({
      key: payCfg.paystack_public_key,
      email,
      amount: (isNg ? p.price : Math.round(p.intl ?? p.price / 1000)) * 100, // kobo (NGN) or cents (USD)
      currency: isNg ? "NGN" : "USD",
      ref: `NM_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
      metadata: { product: p.name, variant },
      callback: (response) => {
        api.get(`/checkout/paystack/verify/${response.reference}`)
          .then(({ data }) => {
            if (data.status === "success") toast.success(`Payment confirmed — ${p.name}. We'll be in touch about delivery!`);
            else toast.warning("Payment received — verification pending. We'll confirm shortly.");
          })
          .catch(() => toast.warning("Payment received — verification pending. We'll confirm shortly."));
      },
      onClose: () => toast.info("Payment window closed — nothing was charged."),
    });
    handler.openIframe();
  };

  const startBuy = (e, p) => {
    e.stopPropagation();
    if (!payCfg?.paystack_public_key) {
      toast.info("Card checkout is being activated — please use WhatsApp checkout from the cart for now.");
      return;
    }
    if (user?.email) return openPaystack(p, user.email);
    setBuyEmail("");
    setBuyItem(p);
  };

  const confirmBuy = () => {
    const email = buyEmail.trim();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      toast.error("Enter a valid email for your receipt.");
      return;
    }
    const item = buyItem;
    setBuyItem(null);
    openPaystack(item, email);
  };

  // WhatsApp order — pre-fills product name, price and (for apparel) the picked size/color.
  const isLocal = (p) => p.local || /^(tee|hoodie|poster)-/.test(p.id || "");
  const orderWhatsApp = (e, p) => {
    e.stopPropagation();
    const price = isLocal(p) ? flatPrice(p) : fmt(p.price);
    const variant = isApparel(p) ? ` (${getPick(p.id).size} / ${getPick(p.id).color})` : "";
    const msg = `Hello NAIJAMAN! I'd like to order:\n\n${p.name}${variant} — ${price}\n\nPlease confirm availability. Thank you!`;
    window.open(`https://wa.me/2349056016627?text=${encodeURIComponent(msg)}`, "_blank");
  };

  useEffect(() => {
    window.scrollTo(0, 0);
    Promise.all([api.get("/products"), api.get("/categories")])
      .then(([p, c]) => { setProducts([...p.data.map(hoodiePriced), ...LOCAL_ACCESSORIES]); setCategories(c.data); })
      .catch(() => {});
  }, []);

  const filtered = useMemo(
    () => (active === "all" ? products : products.filter((p) => p.category === active)),
    [products, active]
  );

  const quickAdd = (e, p) => {
    e.stopPropagation();
    if (isApparel(p)) {
      const { size, color } = getPick(p.id);
      addItem(p, `${size} / ${color}`, 1);
      toast.success(`${p.name} (${size} / ${color}) added to cart`);
    } else if (p.sizes && p.sizes.length > 0) {
      setSelected(p);
    } else {
      addItem(p, null, 1);
      toast.success(`${p.name} added to cart`);
    }
  };

  return (
    <div className="App bg-[#050806] min-h-screen" data-testid="merch-page">
      <Navbar />

      {/* Banner */}
      <section className="relative pt-[68px]">
        <div className="relative h-[46vh] min-h-[340px] overflow-hidden">
          <img src={MERCH_POSTER} alt="NAIJAMAN Merch Line 2026" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#050806] via-[#050806]/50 to-black/40" />
          <div className="absolute inset-0 halftone opacity-20" />
          <div className="absolute inset-0 flex flex-col justify-end max-w-[1500px] mx-auto px-5 sm:px-8 pb-10">
            <motion.p initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.2 }} className="font-accent text-lg tracking-[0.3em] text-emerald-400">OFFICIAL MERCHANDISE</motion.p>
            <motion.h1 initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }} className="comic-title text-5xl sm:text-7xl">NAIJAMAN STORE</motion.h1>
            <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.4 }} className="text-gray-300 mt-2 max-w-xl">Represent. Empower. Protect. Premium apparel, toys, games and gear — shipped worldwide, made for heroes.</motion.p>
          </div>
        </div>
      </section>

      {/* Category filter */}
      <div className="sticky top-[68px] z-30 bg-[#050806]/90 backdrop-blur-lg border-y border-white/10">
        <div className="max-w-[1500px] mx-auto px-5 sm:px-8 py-3 flex gap-2 overflow-x-auto no-scrollbar" data-testid="category-filter">
          {[{ id: "all", name: "All" }, ...categories].map((c) => (
            <button
              key={c.id}
              onClick={() => setActive(c.id)}
              className={`whitespace-nowrap font-accent text-lg tracking-wide px-4 py-1.5 rounded-full border transition-colors ${active === c.id ? "bg-emerald-500 border-emerald-500 text-emerald-950" : "bg-transparent border-white/15 text-gray-300 hover:border-emerald-400"}`}
              data-testid={`cat-${c.id}`}
            >
              {c.name}
            </button>
          ))}
        </div>
      </div>

      {/* Grid */}
      <section className="max-w-[1500px] mx-auto px-5 sm:px-8 py-12">
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5">
          {filtered.map((p, i) => (
            <motion.div
              key={p.id}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-30px" }}
              transition={{ duration: 0.45, delay: (i % 4) * 0.06 }}
              onClick={() => setSelected(p)}
              className="group cursor-pointer rounded-xl overflow-hidden border border-white/10 hover:border-emerald-400 bg-white/5 transition-colors duration-300"
              data-testid={`product-card-${p.id}`}
            >
              <div className="aspect-square bg-white overflow-hidden relative">
                <img src={p.image} alt={p.name} loading="lazy" className="w-full h-full object-contain transition-transform duration-500 group-hover:scale-105" />
                {p.hoverImage && (
                  <img src={p.hoverImage} alt={`${p.name} — contents`} loading="lazy" className="absolute inset-0 w-full h-full object-contain opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                )}
                {p.badge && (
                  <div className="absolute top-3 left-3 bg-amber-400 text-amber-950 font-display text-xs px-2 py-0.5 rounded">{p.badge}</div>
                )}
                <button
                  onClick={(e) => quickAdd(e, p)}
                  className="absolute bottom-3 right-3 w-11 h-11 grid place-items-center rounded-full bg-emerald-500 text-emerald-950 opacity-0 group-hover:opacity-100 translate-y-2 group-hover:translate-y-0 transition-all duration-300 glow-green"
                  data-testid={`quick-add-${p.id}`}
                  aria-label="add to cart"
                >
                  <Plus size={20} />
                </button>
              </div>
              <div className="p-4">
                <p className="text-white font-semibold leading-tight">{p.name}</p>
                <p className="text-emerald-400 font-display text-2xl mt-1">{p.local ? flatPrice(p) : fmt(p.price)}</p>
                {p.blurb && <p className="text-gray-500 text-xs mt-1">{p.blurb}</p>}
                {isApparel(p) ? (
                  <ApparelPickers id={p.id} pick={getPick(p.id)} onPick={(k, v) => setPick(p.id, k, v)} />
                ) : (
                  p.sizes && p.sizes.length > 0 && (
                    <p className="text-gray-500 text-xs mt-1">{p.sizes.length} sizes available</p>
                  )
                )}
                <button
                  onClick={(e) => startBuy(e, p)}
                  className="mt-3 w-full py-2 rounded-lg border border-emerald-500/60 text-emerald-400 font-accent text-sm tracking-[0.2em] hover:bg-emerald-500 hover:text-emerald-950 transition-colors"
                  data-testid={`buy-now-${p.id}`}
                >
                  BUY NOW
                </button>
                <button
                  onClick={(e) => orderWhatsApp(e, p)}
                  className="mt-2 w-full py-2 rounded-lg border border-white/15 text-gray-300 font-accent text-sm tracking-[0.15em] hover:border-emerald-400 hover:text-emerald-400 transition-colors inline-flex items-center justify-center gap-2"
                  data-testid={`whatsapp-order-${p.id}`}
                >
                  <MessageCircle size={15} /> ORDER VIA WHATSAPP
                </button>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* T-Shirts section */}
      <section className="max-w-[1500px] mx-auto px-5 sm:px-8 pb-16" data-testid="tshirts-section">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-40px" }}
          transition={{ duration: 0.6 }}
          className="flex flex-col sm:flex-row sm:items-end justify-between gap-3 mb-8 pt-6 border-t border-white/10"
        >
          <div>
            <p className="font-accent text-xl tracking-[0.3em] text-emerald-400">APPAREL</p>
            <h2 className="font-display text-4xl sm:text-5xl text-white mt-1">T-SHIRTS</h2>
          </div>
          <p className="text-gray-400 max-w-sm text-sm">Premium NAIJAMAN tees. Soft, durable cotton · one flat price everywhere.</p>
        </motion.div>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5" data-testid="tshirts-grid">
          {TSHIRTS.map((p, i) => (
            <motion.div
              key={p.id}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-30px" }}
              transition={{ duration: 0.45, delay: (i % 4) * 0.06 }}
              onClick={() => setPosterView(p)}
              className="group cursor-pointer rounded-xl overflow-hidden border border-white/10 hover:border-emerald-400 bg-white/5 transition-colors duration-300"
              data-testid={`tshirt-card-${p.id}`}
            >
              <div className="aspect-square bg-white overflow-hidden relative">
                <img src={p.image} alt={p.name} loading="lazy" className="w-full h-full object-contain transition-transform duration-500 group-hover:scale-105" />
                <button
                  onClick={(e) => quickAdd(e, p)}
                  className="absolute bottom-3 right-3 w-11 h-11 grid place-items-center rounded-full bg-emerald-500 text-emerald-950 opacity-0 group-hover:opacity-100 translate-y-2 group-hover:translate-y-0 transition-all duration-300 glow-green"
                  data-testid={`quick-add-tee-${p.id}`}
                  aria-label="add to cart"
                >
                  <Plus size={20} />
                </button>
              </div>
              <div className="p-4">
                <p className="text-white font-semibold leading-tight line-clamp-2">{p.name}</p>
                <p className="text-emerald-400 font-display text-2xl mt-1">{flatPrice(p)}</p>
                <ApparelPickers id={p.id} pick={getPick(p.id)} onPick={(k, v) => setPick(p.id, k, v)} />
                <button
                  onClick={(e) => startBuy(e, p)}
                  className="mt-3 w-full py-2 rounded-lg border border-emerald-500/60 text-emerald-400 font-accent text-sm tracking-[0.2em] hover:bg-emerald-500 hover:text-emerald-950 transition-colors"
                  data-testid={`buy-now-${p.id}`}
                >
                  BUY NOW
                </button>
                <button
                  onClick={(e) => orderWhatsApp(e, p)}
                  className="mt-2 w-full py-2 rounded-lg border border-white/15 text-gray-300 font-accent text-sm tracking-[0.15em] hover:border-emerald-400 hover:text-emerald-400 transition-colors inline-flex items-center justify-center gap-2"
                  data-testid={`whatsapp-order-${p.id}`}
                >
                  <MessageCircle size={15} /> ORDER VIA WHATSAPP
                </button>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Hoodies group */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-40px" }}
          transition={{ duration: 0.6 }}
          className="flex flex-col sm:flex-row sm:items-end justify-between gap-3 mt-14 mb-8 pt-6 border-t border-white/10"
          data-testid="hoodies-group"
        >
          <div>
            <p className="font-accent text-xl tracking-[0.3em] text-emerald-400">APPAREL</p>
            <h3 className="font-display text-3xl sm:text-4xl text-white mt-1">HOODIES</h3>
          </div>
          <p className="text-gray-400 max-w-sm text-sm">Heavyweight NAIJAMAN hoodies. Warm, bold, heroic · one flat price everywhere.</p>
        </motion.div>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5" data-testid="hoodies-grid">
          {HOODIES.map((p, i) => (
            <motion.div
              key={p.id}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-30px" }}
              transition={{ duration: 0.45, delay: (i % 4) * 0.06 }}
              onClick={() => setPosterView(p)}
              className="group cursor-pointer rounded-xl overflow-hidden border border-white/10 hover:border-emerald-400 bg-white/5 transition-colors duration-300"
              data-testid={`hoodie-card-${p.id}`}
            >
              <div className="aspect-square bg-white overflow-hidden relative">
                <img src={p.image} alt={p.name} loading="lazy" className="w-full h-full object-contain transition-transform duration-500 group-hover:scale-105" />
                <button
                  onClick={(e) => quickAdd(e, p)}
                  className="absolute bottom-3 right-3 w-11 h-11 grid place-items-center rounded-full bg-emerald-500 text-emerald-950 opacity-0 group-hover:opacity-100 translate-y-2 group-hover:translate-y-0 transition-all duration-300 glow-green"
                  data-testid={`quick-add-hoodie-${p.id}`}
                  aria-label="add to cart"
                >
                  <Plus size={20} />
                </button>
              </div>
              <div className="p-4">
                <p className="text-white font-semibold leading-tight line-clamp-2">{p.name}</p>
                <p className="text-emerald-400 font-display text-2xl mt-1">{flatPrice(p)}</p>
                <ApparelPickers id={p.id} pick={getPick(p.id)} onPick={(k, v) => setPick(p.id, k, v)} />
                <button
                  onClick={(e) => startBuy(e, p)}
                  className="mt-3 w-full py-2 rounded-lg border border-emerald-500/60 text-emerald-400 font-accent text-sm tracking-[0.2em] hover:bg-emerald-500 hover:text-emerald-950 transition-colors"
                  data-testid={`buy-now-${p.id}`}
                >
                  BUY NOW
                </button>
                <button
                  onClick={(e) => orderWhatsApp(e, p)}
                  className="mt-2 w-full py-2 rounded-lg border border-white/15 text-gray-300 font-accent text-sm tracking-[0.15em] hover:border-emerald-400 hover:text-emerald-400 transition-colors inline-flex items-center justify-center gap-2"
                  data-testid={`whatsapp-order-${p.id}`}
                >
                  <MessageCircle size={15} /> ORDER VIA WHATSAPP
                </button>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Posters section */}
      <section className="max-w-[1500px] mx-auto px-5 sm:px-8 pb-16" data-testid="posters-section">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-40px" }}
          transition={{ duration: 0.6 }}
          className="flex flex-col sm:flex-row sm:items-end justify-between gap-3 mb-8 pt-6 border-t border-white/10"
        >
          <div>
            <p className="font-accent text-xl tracking-[0.3em] text-emerald-400">WALL ART</p>
            <h2 className="font-display text-4xl sm:text-5xl text-white mt-1">POSTERS</h2>
          </div>
          <p className="text-gray-400 max-w-sm text-sm">Premium NAIJAMAN prints. Frame-ready · one flat price everywhere.</p>
        </motion.div>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5" data-testid="posters-grid">
          {POSTERS.map((p, i) => (
            <motion.div
              key={p.id}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-30px" }}
              transition={{ duration: 0.45, delay: (i % 4) * 0.06 }}
              onClick={() => setPosterView(p)}
              className="group cursor-pointer rounded-xl overflow-hidden border border-white/10 hover:border-emerald-400 bg-white/5 transition-colors duration-300"
              data-testid={`poster-card-${p.id}`}
            >
              <div className="aspect-square bg-black overflow-hidden relative">
                <img src={p.image} alt={p.name} loading="lazy" className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105" />
                <div className="absolute top-3 left-3 bg-emerald-500 text-emerald-950 font-display text-xs px-2 py-0.5 rounded">POSTER</div>
              </div>
              <div className="p-4">
                <p className="text-white font-semibold leading-tight line-clamp-2">{p.name}</p>
                <p className="text-emerald-400 font-display text-2xl mt-1">{flatPrice(p)}</p>
                <button
                  onClick={(e) => startBuy(e, p)}
                  className="mt-3 w-full py-2 rounded-lg border border-emerald-500/60 text-emerald-400 font-accent text-sm tracking-[0.2em] hover:bg-emerald-500 hover:text-emerald-950 transition-colors"
                  data-testid={`buy-now-${p.id}`}
                >
                  BUY NOW
                </button>
                <button
                  onClick={(e) => orderWhatsApp(e, p)}
                  className="mt-2 w-full py-2 rounded-lg border border-white/15 text-gray-300 font-accent text-sm tracking-[0.15em] hover:border-emerald-400 hover:text-emerald-400 transition-colors inline-flex items-center justify-center gap-2"
                  data-testid={`whatsapp-order-${p.id}`}
                >
                  <MessageCircle size={15} /> ORDER VIA WHATSAPP
                </button>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Poster lightbox */}
      <AnimatePresence>
        {posterView && (
          <motion.div
            className="fixed inset-0 z-[92] bg-black/90 backdrop-blur flex items-center justify-center p-4"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={() => setPosterView(null)}
            data-testid="poster-lightbox"
          >
            <button
              onClick={() => setPosterView(null)}
              className="absolute top-4 right-4 w-11 h-11 grid place-items-center rounded-full bg-white/10 hover:bg-emerald-500 hover:text-emerald-950 text-white border border-white/20"
              aria-label="close"
              data-testid="poster-close"
            >
              <X size={22} />
            </button>
            <motion.div
              initial={{ scale: 0.94, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.94, opacity: 0 }}
              transition={{ duration: 0.25 }}
              onClick={(e) => e.stopPropagation()}
              className="max-w-4xl w-full flex flex-col items-center"
            >
              <img src={posterView.image} alt={posterView.name} className="max-h-[78vh] w-auto rounded-xl border border-white/10 shadow-2xl" />
              <div className="text-center mt-4">
                <p className="font-display text-2xl text-white">{posterView.name}</p>
                <p className="text-emerald-400 font-display text-3xl mt-1">{flatPrice(posterView)}</p>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Buy Now — email capture for guests */}
      <AnimatePresence>
        {buyItem && (
          <motion.div
            className="fixed inset-0 z-[95] bg-black/90 backdrop-blur flex items-center justify-center p-4"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={() => setBuyItem(null)}
            data-testid="buy-email-modal"
          >
            <motion.div
              initial={{ scale: 0.94, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.94, opacity: 0 }}
              transition={{ duration: 0.25 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-[#0a0f0c] border border-white/10 rounded-xl p-6 w-full max-w-sm"
            >
              <h3 className="font-display text-3xl text-white">CHECKOUT</h3>
              <p className="text-gray-400 text-sm mt-1 leading-snug">{buyItem.name}</p>
              <p className="text-emerald-400 font-display text-2xl mt-2">{currency === "NGN" ? `₦${buyItem.price.toLocaleString()}` : `$${buyItem.intl ?? Math.round(buyItem.price / 1000)}`}</p>
              <label htmlFor="buy-email" className="block text-gray-300 text-sm mt-4 mb-1.5">Email for your receipt</label>
              <input
                id="buy-email"
                type="email"
                value={buyEmail}
                onChange={(e) => setBuyEmail(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && confirmBuy()}
                placeholder="you@example.com"
                className="w-full bg-white/5 border border-white/15 rounded-lg px-4 py-2.5 text-white placeholder-gray-600 focus:border-emerald-400 outline-none"
                data-testid="buy-email-input"
              />
              <button
                onClick={confirmBuy}
                className="mt-4 w-full py-3 rounded-lg bg-emerald-500 text-emerald-950 font-accent text-lg tracking-[0.15em] hover:bg-emerald-400 transition-colors glow-green"
                data-testid="buy-email-continue"
              >
                CONTINUE TO PAYMENT
              </button>
              <p className="text-gray-600 text-xs mt-3 text-center">Secure card payment by Paystack · charged in {currency === "NGN" ? "Naira" : "USD"}</p>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <Footer />
      {selected && <ProductModal product={selected} onClose={() => setSelected(null)} />}
    </div>
  );
}
